import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { notificationService } from '@services/api/notifications'
import toast from 'react-hot-toast'

// Formate une date en temps relatif (maintenant, 27min, 2h, 3j, 04 juin)
function formatRelativeTime(d) {
  if (!d) return ''
  const date = new Date(d)
  if (isNaN(date)) return ''
  const diff = Date.now() - date
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const dy = Math.floor(diff / 86400000)
  if (m < 1) return 'maintenant'
  if (m < 60) return `${m}min`
  if (h < 24) return `${h}h`
  if (dy < 7) return `${dy}j`
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function NotificationsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, unread, read

  // Charger les notifications depuis l'API
  const loadNotifications = async (silent = false) => {
    if (!user?.client_id) return
    
    try {
      if (!silent) setLoading(true)
      const response = await notificationService.getNotifications(user.client_id, filter)
      if (response.success) {
        setNotifications(response.data)
      }
    } catch (error) {
      console.error('Erreur chargement notifications:', error)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
    
    // Rafraîchir toutes les 10 secondes
    const interval = setInterval(() => {
      loadNotifications(true)
    }, 10000)
    
    return () => clearInterval(interval)
  }, [user?.client_id, filter])

  const handleMarkAsRead = async (id) => {
    try {
      const response = await notificationService.markAsRead(id)
      if (response.success) {
        setNotifications(notifications.map(notif =>
          notif.id === id ? { ...notif, is_read: true } : notif
        ))
        // Mettre à jour le badge du header
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur marquage comme lu:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationService.markAllRead()
      if (response.success) {
        setNotifications(notifications.map(notif => ({ ...notif, is_read: true })))
        toast.success('Toutes les notifications ont été marquées comme lues')
        // Mettre à jour le badge du header
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur marquage tout comme lu:', error)
    }
  }

  const handleDeleteNotification = async (id) => {
    try {
      const response = await notificationService.deleteNotification(id)
      if (response.success) {
        setNotifications(notifications.filter(notif => notif.id !== id))
        toast.success('Notification supprimée')
        window.dispatchEvent(new Event('reloadNotifications'))
      }
    } catch (error) {
      console.error('Erreur suppression notification:', error)
    }
  }

  const handleNotificationClick = async (notif) => {
    await handleMarkAsRead(notif.id)
    if (notif.link) {
      navigate(notif.link)
    }
  }

  const filteredNotifications = notifications

  const unreadCount = notifications.filter(n => !n.is_read).length

  // Icône SVG + couleurs selon le type de notification
  const renderIcon = (type) => {
    const config = {
      like: {
        bg: 'bg-red-100', text: 'text-red-500',
        path: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
      },
      comment: {
        bg: 'bg-blue-100', text: 'text-blue-500',
        path: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      },
      new_post: {
        bg: 'bg-[#FFD700]', text: 'text-[#002A4E]',
        path: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
      },
    }
    const c = config[type] || {
      bg: 'bg-gray-100', text: 'text-gray-500',
      path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    }
    return (
      <div className={`w-10 h-10 ${c.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
        <svg className={`w-6 h-6 ${c.text}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d={c.path} />
        </svg>
      </div>
    )
  }

  const FILTERS = [
    ['all', 'Toutes', notifications.length],
    ['unread', 'Non lues', unreadCount],
    ['read', 'Lues', notifications.length - unreadCount],
  ]

  return (
    <div className="home-font min-h-screen bg-slate-50 py-8">
      <div className="w-[94%] md:w-[80%] mx-auto">

        {/* En-tête */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Mon compte</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">Notifications</h1>
            <p className="mt-1 text-slate-500">
              {unreadCount > 0
                ? <>Vous avez <span className="font-bold text-[#002A4E]">{unreadCount}</span> notification{unreadCount > 1 ? 's' : ''} non lue{unreadCount > 1 ? 's' : ''}.</>
                : 'Vous êtes à jour.'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="inline-flex items-center gap-2 self-start sm:self-auto rounded-md bg-[#FFD700] px-4 py-2.5 text-sm font-bold text-[#002A4E] shadow-sm shadow-amber-300/60 transition-colors hover:bg-[#F5C800]"
            >
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              Tout marquer comme lu
            </button>
          )}
        </div>

        {/* Filtres */}
        <div className="mb-6 flex gap-1 overflow-x-auto scrollbar-hide border-b border-slate-200">
          {FILTERS.map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`relative inline-flex flex-shrink-0 items-center gap-2 px-4 py-3 text-sm font-bold transition-colors ${
                filter === key ? 'text-[#002A4E]' : 'text-slate-400 hover:text-[#002A4E]'
              }`}
            >
              {label}
              <span className={`rounded-md px-1.5 py-0.5 text-xs ${filter === key ? 'bg-[#FFD700] text-[#002A4E]' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
              {filter === key && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-[#F5B700]" />}
            </button>
          ))}
        </div>

        {/* Liste des notifications */}
        <div className="space-y-3">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="rounded-md bg-white p-4 ring-1 ring-slate-200/70">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 flex-shrink-0 rounded-full skeleton-shimmer" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 rounded skeleton-shimmer" />
                    <div className="h-3 w-1/2 rounded skeleton-shimmer" />
                  </div>
                </div>
              </div>
            ))
          ) : filteredNotifications.length === 0 ? (
            <div className="rounded-md bg-white px-6 py-16 text-center ring-1 ring-slate-200/70">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-md bg-[#FFD700] text-[#002A4E]">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
              </span>
              <p className="mt-4 font-bold text-[#002A4E]">
                {notifications.length > 0 ? 'Aucune notification dans cette catégorie' : 'Aucune notification'}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {filter === 'unread' ? 'Vous avez tout lu !' : 'Les likes, commentaires et nouveautés apparaîtront ici.'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                className={`relative overflow-hidden rounded-md bg-white ring-1 transition-shadow hover:shadow-md ${
                  notif.is_read ? 'ring-slate-200/70' : 'ring-amber-200 bg-[#FFFDF3]'
                }`}
              >
                {!notif.is_read && <span className="absolute inset-y-0 left-0 w-1 bg-[#F5B700]" />}
                <div className="flex items-start gap-4 p-4 sm:p-5">
                  {renderIcon(notif.type)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-bold text-[#002A4E]">{notif.title}</h3>
                      <span className="flex-shrink-0 text-xs text-slate-400">{formatRelativeTime(notif.timestamp || notif.created_at)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{notif.message}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {notif.link && (
                        <button
                          onClick={() => handleNotificationClick(notif)}
                          className="inline-flex items-center gap-1 rounded-md bg-[#002A4E] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#003866]"
                        >
                          Voir
                        </button>
                      )}
                      {!notif.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(notif.id)}
                          className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 transition-colors hover:text-[#002A4E] hover:ring-amber-300"
                        >
                          Marquer comme lu
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteNotification(notif.id)}
                        className="rounded-md px-3 py-1.5 text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsPage